// src/iap/initPurchases.ts
import axios from 'axios';
import * as appActions from '../Actions/app';
import { reportError } from 'Utils/reportError';
declare global {
  interface Window {
    CdvPurchase?: CdvPurchaseApi;
    cordova?: { exec?: CordovaExec };
    Capacitor: unknown;
    __iapInitialized?: boolean;
    __iapSetupPromise?: Promise<CdvPurchaseApi | undefined>;
    __iapLastVerifiedReceiptHash?: string;
    __iapVerifyArmedUntil?: number;
    __iapVerifyArmedProductId?: string;
    __iapLastProductLoadErrors?: CdvStoreError[];
  }
}

type MinimalUser = { id?: string | number; user_id?: string; email?: string | null } | null;
type Dispatch = (a: unknown) => void;
type CordovaExec = (
  success: (res: unknown) => void,
  error: (e: unknown) => void,
  service: string,
  action: string,
  args: unknown[],
) => void;

export type CdvStoreError = {
  isError?: boolean;
  code?: string | number;
  message?: string;
  productId?: string | null;
};

export type CdvPurchaseOffer = {
  productId: string;
  platform: unknown;
  order?: (additionalData?: unknown) => Promise<CdvStoreError | undefined>;
  canPurchase?: boolean;
};

export type CdvPurchaseProduct = {
  id: string;
  canPurchase?: boolean;
  getOffer?: (id?: string) => CdvPurchaseOffer | undefined;
};

export type CdvPurchaseStore = {
  verbosity?: unknown;
  minTimeBetweenUpdates?: number;
  applicationUsername?: () => string;
  register: (products: unknown[]) => void;
  when: () => { approved: (cb: (tx: CdvPurchaseTransaction) => void | Promise<void>) => void };
  error: (cb: (e: unknown) => void) => void;
  ready: (cb: () => void) => void;
  initialize: (platforms: unknown[]) => Promise<unknown>;
  update: () => Promise<unknown>;
  get: (id: string, platform?: unknown) => CdvPurchaseProduct | undefined;
  getReceipt?: (platform: unknown) => Promise<unknown>;
  order: (offer: CdvPurchaseOffer, additionalData?: unknown) => Promise<CdvStoreError | undefined>;
};

export type CdvPurchaseApi = {
  store: CdvPurchaseStore;
  ProductType: { PAID_SUBSCRIPTION: unknown };
  Platform: { APPLE_APPSTORE: unknown };
  LogLevel?: { DEBUG: unknown };
};

type CdvPurchaseTransaction = {
  products?: { id: string }[];
  receipt?: unknown;
  transactionId?: string;
  originalTransactionId?: string;
  platform?: unknown;
  finish: () => void;
};

const LOG = '[IAP]';
const log = (...a: unknown[]) => console.log(LOG, ...a);
const warn = (...a: unknown[]) => console.warn(LOG, ...a);
const err = (...a: unknown[]) => console.error(LOG, ...a);

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
const IAP_VERIFY_ARM_WINDOW_MS = 15 * 60 * 1000;
const IAP_PLUGIN_WAIT_TIMEOUT_MS = 10 * 1000;
const IAP_PLUGIN_WAIT_INTERVAL_MS = 100;
const CORDOVA_DEVICE_READY_TIMEOUT_MS = 10 * 1000;

export const APPLE_IAP_PRODUCT_IDS = [
  'com.museflow.monthly',
  'com.museflow.3months',
  'com.museflow.annual',
] as const;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isStoreError = (value: unknown): value is CdvStoreError => {
  const error = value as CdvStoreError;
  return (
    !!error && typeof error === 'object' && (!!error.message || !!error.code || !!error.isError)
  );
};

function storeErrorsFrom(result: unknown): CdvStoreError[] {
  if (Array.isArray(result)) return result.filter(isStoreError);
  return isStoreError(result) ? [result] : [];
}

function getCdvPurchase(): CdvPurchaseApi | undefined {
  const Cdv = window.CdvPurchase;
  if (Cdv?.store && Cdv?.Platform?.APPLE_APPSTORE && Cdv?.ProductType?.PAID_SUBSCRIPTION) {
    return Cdv;
  }
  return undefined;
}

async function waitForCdvPurchase(
  timeoutMs: number = IAP_PLUGIN_WAIT_TIMEOUT_MS,
): Promise<CdvPurchaseApi | undefined> {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const Cdv = getCdvPurchase();
    if (Cdv) return Cdv;
    await delay(IAP_PLUGIN_WAIT_INTERVAL_MS);
  }
  return getCdvPurchase();
}

function waitForDeviceReady(timeoutMs: number = CORDOVA_DEVICE_READY_TIMEOUT_MS) {
  if (!window.cordova) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    document.addEventListener('deviceready', finish, { once: true });
    window.setTimeout(() => {
      if (!settled) {
        reportError(
          new Error(`'deviceready' not fired within ${timeoutMs}ms; proceeding without it`),
          'iap.deviceReady',
        );
      }
      finish();
    }, timeoutMs);
  });
}

export function armIAPReceiptVerification(productId: string) {
  const expiresAt = Date.now() + IAP_VERIFY_ARM_WINDOW_MS;
  window.__iapVerifyArmedUntil = expiresAt;
  window.__iapVerifyArmedProductId = productId;
  log('verify arm set for product', productId, 'until', new Date(expiresAt).toISOString());
}

export function clearIAPReceiptVerificationArm() {
  window.__iapVerifyArmedUntil = undefined;
  window.__iapVerifyArmedProductId = undefined;
}

function isReceiptVerificationArmed(productIds: string[]): boolean {
  const armedUntil = window.__iapVerifyArmedUntil ?? 0;
  if (!armedUntil) return false;

  if (Date.now() > armedUntil) {
    log('verify arm expired, clearing arm');
    clearIAPReceiptVerificationArm();
    return false;
  }

  const armedProductId = window.__iapVerifyArmedProductId;
  if (armedProductId && productIds.length > 0 && !productIds.includes(armedProductId)) {
    log('verify arm mismatch, skipping verify', {
      armedProductId,
      approvedProductIds: productIds,
    });
    return false;
  }

  return true;
}

const looksB64 = (s: unknown): s is string =>
  typeof s === 'string' && s.length > 200 && /^[A-Za-z0-9+/=]+$/.test(s);

function pickB64(obj: unknown): string | undefined {
  if (!obj) return;
  if (looksB64(obj)) return obj;
  const keys = [
    'appStoreReceipt',
    'appStoreReceiptBase64',
    'latestReceipt',
    'receipt',
    'receipt_data',
    'base64',
    'data',
    'raw',
  ];
  const o = obj as Record<string, unknown>;
  for (const k of keys) if (looksB64(o?.[k])) return o[k] as string;
  for (const v of Object.values(o)) if (looksB64(v)) return v as string;
  return;
}

// Tiny cheap hash for dedupe (NOT cryptographic; just for “don’t spam verify”).
function cheapHash(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  // unsigned
  return (h >>> 0).toString(16);
}

function getAppReceiptViaExec(): Promise<string | undefined> {
  return new Promise((resolve) => {
    const exec = window?.cordova?.exec;
    if (!exec) {
      warn('cordova.exec not available');
      return resolve(undefined);
    }
    log('exec → InAppPurchase.appStoreReceipt');
    exec(
      (res: unknown) => {
        const b64 = pickB64(res);
        log('exec ← appStoreReceipt:', b64 ? `ok(len=${b64.length})` : `no-b64`);
        resolve(b64);
      },
      (e: unknown) => {
        warn('exec appStoreReceipt failed', e);
        resolve(undefined);
      },
      'InAppPurchase',
      'appStoreReceipt',
      [],
    );
  });
}

async function getReceiptB64(Cdv: CdvPurchaseApi): Promise<string | undefined> {
  const { store, Platform } = Cdv;
  // Preferred: v13+ getter
  if (typeof store?.getReceipt === 'function') {
    try {
      const r = await store.getReceipt(Platform.APPLE_APPSTORE);
      const b64 = pickB64(r);
      log('store.getReceipt →', b64 ? `ok(len=${b64.length})` : 'no-b64');
      if (b64) return b64;
    } catch (e) {
      warn('store.getReceipt threw', e);
    }
  } else {
    log('store.getReceipt is undefined (JS bundled incorrectly or old adapter).');
  }
  // Fallback: call native directly
  return getAppReceiptViaExec();
}

async function verify(
  base64: string,
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
  meta?: { reason?: 'approved'; txId?: string; origTxId?: string },
) {
  try {
    const token = (await currentAuthToken?.()) || '';
    if (!token) {
      warn('verify skipped: no auth token');
      return;
    }

    // Dedupe per receipt blob
    const h = cheapHash(base64);
    if (window.__iapLastVerifiedReceiptHash === h) {
      log('verify skipped: same receipt hash already verified');
      return;
    }
    window.__iapLastVerifiedReceiptHash = h;

    const url = `${baseUrl}receipts/verify`;
    const { data, status } = await axios.post(
      url,
      {
        platform: 'apple',
        receipt_data: base64,
        reason: meta?.reason,
        tx_id: meta?.txId,
        original_tx_id: meta?.origTxId,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    log('verify ←', status, 'active=', !!data?.active, 'env=', data?.environment);
    if (data?.active) {
      dispatch(
        appActions.setUserData({
          ...(userData || {}),
          subscription_status: 'active',
        }),
      );
    } else {
      log('verify returned inactive receipt; skipping local subscription mutation');
    }
  } catch (e: unknown) {
    const errObj = e as { response?: { status?: number }; message?: string };
    err('verify failed', errObj?.response?.status, errObj?.message);
    reportError(e, 'iap.verify');
  }
}

export function setupIAP(
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
) {
  if (window.__iapInitialized) {
    log('already initialized');
    return Promise.resolve(getCdvPurchase());
  }

  if (window.__iapSetupPromise) {
    log('initialization already in progress');
    return window.__iapSetupPromise;
  }

  window.__iapSetupPromise = (async () => {
    const Cdv = await waitForCdvPurchase();
    if (!Cdv) {
      err('CdvPurchase.store missing after waiting for Cordova purchase plugin');
      reportError(
        new Error(
          `CdvPurchase.store missing after waiting ${IAP_PLUGIN_WAIT_TIMEOUT_MS}ms for Cordova purchase plugin`,
        ),
        'iap.pluginWait',
      );
      return undefined;
    }

    await waitForDeviceReady();

    const { store, ProductType, Platform, LogLevel } = Cdv;
    try {
      store.verbosity = LogLevel?.DEBUG;
    } catch {
      // intentionally ignore
    }

    store.applicationUsername = () =>
      (userData?.id ?? userData?.user_id ?? userData?.email ?? '') + '';

    store.register([
      {
        id: APPLE_IAP_PRODUCT_IDS[0],
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.APPLE_APPSTORE,
        group: 'museflow',
      },
      {
        id: APPLE_IAP_PRODUCT_IDS[1],
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.APPLE_APPSTORE,
        group: 'museflow',
      },
      {
        id: APPLE_IAP_PRODUCT_IDS[2],
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.APPLE_APPSTORE,
        group: 'museflow',
      },
    ]);

    store.when().approved(async (tx: CdvPurchaseTransaction) => {
      const txProductIds = (tx?.products || []).map((p) => p.id);
      log('approved:', {
        txId: tx?.transactionId,
        orig: tx?.originalTransactionId,
        plat: tx?.platform,
        products: txProductIds,
      });

      try {
        if (!isReceiptVerificationArmed(txProductIds)) {
          log('approved: skipping verify (not armed by user purchase action)');
        } else {
          // Prefer receipt attached to tx, else pull app receipt
          let b64 = pickB64(tx?.receipt);
          log('approved: tx.receipt present?', !!b64);
          if (!b64) b64 = await getReceiptB64(Cdv);

          if (b64) {
            await verify(b64, userData, dispatch, currentAuthToken, {
              reason: 'approved',
              txId: tx?.transactionId,
              origTxId: tx?.originalTransactionId,
            });
          } else {
            warn('approved: no app receipt available to verify');
          }
          clearIAPReceiptVerificationArm();
        }
      } finally {
        try {
          tx.finish();
          log('approved: finish()');
        } catch (e) {
          err('finish() failed', e);
        }
        try {
          await store.update();
          log('approved: store.update()');
        } catch {
          // intentionally ignore
        }
      }
    });

    store.error((e: unknown) => err('Store Error:', e));
    store.ready(() => log('Store ready'));
    window.__iapInitialized = true;

    try {
      log('initialize([APPLE])');
      const initializeResult = await store.initialize([Platform.APPLE_APPSTORE]);
      const initializeErrors = storeErrorsFrom(initializeResult);
      if (initializeErrors.length > 0) {
        window.__iapLastProductLoadErrors = initializeErrors;
        warn('initialize returned product load errors', initializeErrors);
      }
      log('force product refresh after initialize');
      await forceIAPProductRefresh(Cdv);

      // IMPORTANT: removed cold-start verification
      // We only verify on actual approved transactions.
      log('startup: skipping verify (only verifying on purchase approval)');
    } catch (e) {
      err('initialize/update failed', e);
    }

    return Cdv;
  })().finally(() => {
    window.__iapSetupPromise = undefined;
  });

  return window.__iapSetupPromise;
}

export async function ensureIAPReady(
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
): Promise<CdvPurchaseApi | undefined> {
  const Cdv = await setupIAP(userData, dispatch, currentAuthToken);
  if (!Cdv) return undefined;

  try {
    await forceIAPProductRefresh(Cdv);
  } catch (e) {
    warn('ensureIAPReady: product refresh failed', e);
  }

  return Cdv;
}

export function appleProductIdForPlan(planSelect: string) {
  return planSelect === 'monthly'
    ? APPLE_IAP_PRODUCT_IDS[0]
    : planSelect === '3-months'
      ? APPLE_IAP_PRODUCT_IDS[1]
      : planSelect === 'annual'
        ? APPLE_IAP_PRODUCT_IDS[2]
        : '';
}

export function appleProductStates(Cdv: CdvPurchaseApi) {
  const { store, Platform } = Cdv;
  return APPLE_IAP_PRODUCT_IDS.map((id) => {
    const product = store.get(id, Platform.APPLE_APPSTORE);
    return {
      id,
      returned: !!product,
      canPurchase: !!product?.canPurchase,
    };
  });
}

export async function forceIAPProductRefresh(Cdv: CdvPurchaseApi) {
  const store = Cdv.store as CdvPurchaseStore & { lastUpdate?: number };
  const previousMinTimeBetweenUpdates = store.minTimeBetweenUpdates;

  try {
    store.minTimeBetweenUpdates = 0;
    store.lastUpdate = 0;
    const updateResult = await store.update();
    const updateErrors = storeErrorsFrom(updateResult);
    if (updateErrors.length > 0) {
      window.__iapLastProductLoadErrors = updateErrors;
      warn('product refresh returned errors', updateErrors);
    }
  } finally {
    store.minTimeBetweenUpdates = previousMinTimeBetweenUpdates;
  }
}
